/**
 * The section components, and the registry that maps a stored `type` to one.
 *
 * This file is the render half of the composition engine. `SECTION_TYPES` in the shared
 * package is the authority on what exists; `REGISTRY` at the bottom must cover it, and
 * a missing entry is caught by a type error there rather than by a blank page in
 * production.
 *
 * Every component takes `{ payload, resolved }` — the editor's settings, and whatever
 * records the server selected for it. None of them fetch. That is what makes a page one
 * request instead of one per section; see `services/sections.ts` on the API side.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type SectionType } from '@serendib/shared';
import { Markdown } from '../lib/markdown.js';
import { Money, StockPill } from '../components/ui.jsx';
import { ProductCardView } from '../components/cards.jsx';
import { CoverArtwork, PersonArtwork } from '../components/Artwork.jsx';
import type { ProductCard, ProjectCard, ServiceCard, SectionData } from '../lib/queries.js';
import { ContactFormSection, NewsletterSection } from './Forms.jsx';
import { Configurator, ContactSplit, DeploymentRecord, Practice } from './Counter.jsx';
import { PosTerminal } from '../components/PosTerminal.jsx';
import { panelClass, type Ground } from './frame.js';
import { useCounter } from '../lib/stores.js';
import { useState } from 'react';

/* -------------------------------------------------------------------------- */
/* Shared bits                                                                 */
/* -------------------------------------------------------------------------- */

interface Cta { label: string; href: string; style?: 'primary' | 'secondary' | 'ghost' }

function CtaLink({ cta, className }: { cta: Cta | null | undefined; className?: string }) {
  if (!cta?.label || !cta.href) return null;
  const variant = cta.style === 'secondary' ? 'btn--secondary' : cta.style === 'ghost' ? 'btn--ghost' : 'btn--primary';
  const external = /^https?:\/\//i.test(cta.href);
  if (external) {
    return <a className={`btn ${variant} ${className ?? ''}`} href={cta.href} target="_blank" rel="noreferrer noopener">{cta.label}</a>;
  }
  return <Link className={`btn ${variant} ${className ?? ''}`} to={cta.href}>{cta.label}</Link>;
}

/** Eyebrow + heading + body, the block most sections open with. */
function Heading({
  eyebrow,
  heading,
  body,
  align = 'left',
  as: Tag = 'h2',
}: {
  eyebrow?: string | null;
  heading?: string | null;
  body?: string | null;
  align?: 'left' | 'center';
  as?: 'h1' | 'h2';
}) {
  if (!eyebrow && !heading && !body) return null;
  return (
    <div className="stack" style={{ gap: 12, textAlign: align, justifyItems: align === 'center' ? 'center' : undefined }}>
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      {heading ? <Tag>{heading}</Tag> : null}
      {body ? <p className="lead measure" style={{ marginInline: align === 'center' ? 'auto' : undefined }}>{body}</p> : null}
    </div>
  );
}

/**
 * A note on `auto-fit` vs `auto-fill` in this file.
 *
 * The grids BELOW are fed by a section's `limit`, which an editor sets. The count is
 * therefore deliberate and the row should always be full, so they use `auto-fit`: the
 * empty tracks collapse and the cards stretch to fill whatever width they are given.
 *
 * The card grids on the PAGES (`grid--cards` in base.css — the shop listing, the
 * projects index, related items) are the opposite case: a filter or a category decides
 * how many there are, it can be one, and a single card stretched across a whole row
 * looks broken. Those use `auto-fill` so a lone card stays card-sized.
 *
 * These were briefly made the same and it showed: the home page's four-product strip
 * sat in a container wide enough for FIVE 215px tracks, so `auto-fill` kept the fifth
 * track empty and left 257px of dead space after the last card.
 */

/** A section wrapper: the panel, the width, the rhythm. */
function Panel({ children, dark, flush, id, ground }: { children: ReactNode; dark?: boolean; flush?: boolean; id?: string; ground?: Ground }) {
  return (
    <section className="section wrap" id={id}>
      <div className={panelClass({ ground, dark, flush })}>{children}</div>
    </section>
  );
}

type P = Record<string, any>;

/* ========================================================================== */
/* Hero                                                                       */
/* ========================================================================== */

function Hero({ payload }: { payload: P }) {
  const hasVisual = payload.visual !== 'none';
  const showCounter = payload.visual === 'counter';

  // The model is driven by the configurator further down the page — that is what
  // "drives the model above" means in the design. Reading the store here rather than
  // passing props keeps the two sections independent siblings in a list rendered from
  // the database, which is the only relationship they can actually have.
  const choices = useCounter((s) => s.choices);
  const counterConfig = {
    printer: choices.printer !== 'skip',
    drawer: choices.drawer !== 'skip',
    scanner: choices.scanner === 'include',
  };

  const figureLabel = [
    choices.terminal === 'handheld' ? 'Handheld Android' : choices.terminal === 'single' ? '15in single screen' : '15.6in dual display',
    counterConfig.printer ? 'printer' : null,
    counterConfig.drawer ? 'drawer' : null,
    counterConfig.scanner ? 'scanner' : null,
  ].filter(Boolean).join(' + ');

  /**
   * Two layouts, not one grid with a hole in it.
   *
   * The hero was always a two-column grid, and the figure column was simply omitted
   * when `visual` is `none`. That left the panel at 48% of the width with the other
   * half empty — on `/services`, `/about`, `/contact` and `/quote`, every page a
   * visitor reaches from the navigation. It read as a figure that had failed to load.
   *
   * Without a figure the panel takes the full width and splits its own CONTENT into
   * two columns instead: the heading on the left, the body and the calls to action on
   * the right, baseline-aligned. The heading keeps a sane measure either way, which a
   * full-bleed single column would not.
   */
  return (
    <section className="section wrap">
      <div className={hasVisual ? 'hero-grid' : 'hero-grid hero-grid--solo'}>
        <div className="panel hero-copy">
          <div className="hero-copy__lead">
            {payload.eyebrow ? <p className="hero-eyebrow">{payload.eyebrow}</p> : null}
            {payload.heading ? <h1 className="display" style={{ marginTop: 24 }}>{payload.heading}</h1> : null}
          </div>

          <div className="hero-copy__tail">
            {payload.body ? <p className="lead">{payload.body}</p> : null}
            {payload.primaryCta || payload.secondaryCta ? (
              <div className="row" style={{ gap: 10 }}>
                <CtaLink cta={payload.primaryCta} />
                <CtaLink cta={payload.secondaryCta} />
              </div>
            ) : null}

            {/* The figures. They were buried in the middle of the page, which is the one
                place a first-time visitor will not have reached yet — and they are the
                argument: ten years, 140 installs, parts on a shelf in Homagama. */}
            {Array.isArray(payload.facts) && payload.facts.length > 0 ? (
              <ul className="hero-facts">
                {payload.facts.map((fact: { value: string; label: string }) => (
                  <li key={fact.label}>
                    <span className="hero-facts__value">{fact.value}</span>
                    <span className="hero-facts__label">{fact.label}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        {hasVisual ? (
          <div className="hero-figure">
            {showCounter ? (
              <PosTerminal config={counterConfig} label={figureLabel} />
            ) : payload.media?.url ? (
              <img src={payload.media.url} alt={payload.media.alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <PosTerminal config={counterConfig} label={figureLabel} />
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function Marquee({ payload }: { payload: P }) {
  const items: string[] = payload.items ?? [];
  if (items.length === 0) return null;

  return (
    <section className="section wrap" aria-hidden="true">
      <div style={{ overflow: 'hidden', padding: '10px 0' }}>
        {/* Rendered twice with the track translating -50%, which makes the loop
            seamless. The whole strip is aria-hidden: it is decorative, the phrases
            repeat elsewhere on the page, and a screen reader announcing them twice in a
            loop is worse than not announcing them at all. */}
        <div className="marquee-track" style={{ animationDuration: `${payload.speedSeconds ?? 46}s`, fontSize: 'var(--t-sm)', color: 'var(--ink-2)' }}>
          {[0, 1].map((copy) => (
            <span key={copy} style={{ display: 'flex', alignItems: 'center', gap: 22, paddingRight: 22 }}>
              {items.map((item, i) => (
                <span key={`${copy}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 22, whiteSpace: 'nowrap' }}>
                  {item}
                  <span style={{ color: 'var(--accent)', fontSize: 11 }}>&#9679;</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustBar({ payload }: { payload: P }) {
  const names: string[] = payload.names ?? [];
  if (names.length === 0) return null;

  return (
    <section className="section wrap">
      <div className="panel" style={{ padding: '20px 32px' }}>
        <div className="row" style={{ gap: 28, justifyContent: 'space-between' }}>
          {payload.label ? <p className="xs dim" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>{payload.label}</p> : null}
          <div className="row" style={{ gap: 26 }}>
            {names.map((name) => (
              <span key={name} className="small" style={{ fontWeight: 600, color: 'var(--ink-2)' }}>{name}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========================================================================== */
/* Capabilities accordion                                                     */
/* ========================================================================== */

function Capabilities({ payload }: { payload: P }) {
  const items: { title: string; body: string; tags: string[] }[] = payload.items ?? [];
  // The first row opens by default: an accordion where everything is shut looks like a
  // list of headings and gives no clue there is anything behind them.
  const [open, setOpen] = useState(0);

  return (
    <Panel id="capabilities" ground={payload.ground}>
      <div className="cap-grid">
        <div className="cap-aside">
          <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          {items.map((item, index) => {
            const isOpen = open === index;
            return (
              <div key={index} className={`cap-row ${isOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="cap-head"
                  onClick={() => setOpen(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                >
                  <span className="badge-num">{String(index + 1).padStart(2, '0')}</span>
                  <span style={{ fontSize: 'var(--t-h3)', letterSpacing: '-0.025em', fontWeight: 600, lineHeight: 1.25 }}>
                    {item.title}
                  </span>
                  <span className="cap-sign" aria-hidden="true">+</span>
                </button>

                {isOpen ? (
                  <div className="cap-body">
                    <p style={{ fontSize: 16.5, lineHeight: 1.6, color: 'var(--ink-2)', maxWidth: '56ch', textWrap: 'pretty' }}>
                      {item.body}
                    </p>
                    {item.tags?.length ? (
                      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {item.tags.map((tag) => <span key={tag} className="pill">{tag}</span>)}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

function ServicesGrid({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const services = (resolved as ServiceCard[]) ?? [];
  if (services.length === 0) return null;

  return (
    <Panel id="capabilities" ground={payload.ground}>
      <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
      {/*
        The track width decides whether the last row is orphaned. At a 240px minimum
        only four of the five service cards fitted the panel, so the fifth sat alone on
        a second row beside three empty columns. 210px fits all five across, and the
        cards carry a title and two short lines, which does not need 240.
      */}
      <div className="grid" style={{ marginTop: 34, gridTemplateColumns: `repeat(auto-fit, minmax(${payload.columns === 2 ? 320 : 210}px, 1fr))` }}>
        {services.map((service) => (
          <Link
            key={service.id}
            to={`/services/${service.slug}`}
            style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 26, background: 'var(--panel-2)', borderRadius: 'var(--r-card)', color: 'var(--ink)' }}
          >
            <h3>{service.title}</h3>
            <p className="small muted">{service.summary}</p>
            <span className="small" style={{ color: 'var(--accent-deep)', fontWeight: 600, marginTop: 'auto', paddingTop: 4 }}>
              {payload.ctaLabel || 'Learn more'} →
            </span>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

function FeaturedProject({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const project = resolved as ProjectCard | null;
  if (!project) return null;

  const imageFirst = payload.layout !== 'image-right';

  return (
    <Panel ground={payload.ground}>
      <div className={`grid media-split wide-text${imageFirst ? '' : ' media-right'}`}>
        <div className="media" style={{ borderRadius: 'var(--r-card)', overflow: 'hidden', background: 'var(--panel-2)', aspectRatio: '4 / 3' }}>
          {project.coverImageUrl ? (
            <img src={project.coverImageUrl} alt={project.coverImageAlt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <CoverArtwork seed={project.slug} label={project.title} />
          )}
        </div>

        <div className="stack" style={{ gap: 18 }}>
          {payload.eyebrow || project.client ? (
            <p className="eyebrow">{payload.eyebrow ?? 'Our work'}{project.client ? ` · ${project.client}` : ''}</p>
          ) : null}
          <h2>{project.title}</h2>
          <p className="lead">{project.summary}</p>

          {project.outcomes?.length ? (
            <ul className="stack" style={{ gap: 9, marginTop: 4 }}>
              {project.outcomes.slice(0, 4).map((outcome) => (
                <li key={outcome} className="row" style={{ gap: 10, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                  <span aria-hidden="true" style={{ color: 'var(--accent-deep)', fontWeight: 700, lineHeight: 1.5 }}>✓</span>
                  <span className="small muted">{outcome}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="row" style={{ marginTop: 6 }}>
            <Link className="btn btn--secondary" to={`/projects/${project.slug}`}>
              {payload.ctaLabel || 'Read the full story'}
            </Link>
          </div>
        </div>
      </div>
    </Panel>
  );
}

function ProjectGrid({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const projects = (resolved as ProjectCard[]) ?? [];
  if (projects.length === 0) return null;

  return (
    <Panel ground={payload.ground}>
      <div className="spread">
        <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
        {payload.ctaLabel && payload.ctaHref ? (
          <Link className="small" style={{ color: 'var(--accent-deep)', fontWeight: 600 }} to={payload.ctaHref}>
            {payload.ctaLabel} →
          </Link>
        ) : null}
      </div>

      <div className="grid" style={{ marginTop: 32, gridTemplateColumns: `repeat(auto-fit, minmax(${payload.columns === 2 ? 320 : 260}px, 1fr))` }}>
        {projects.map((project) => (
          <Link key={project.id} to={`/projects/${project.slug}`} className="card" style={{ color: 'var(--ink)', background: 'var(--panel-2)' }}>
            <div style={{ aspectRatio: '16 / 10', background: 'var(--wash)' }}>
              {project.coverImageUrl ? (
                <img src={project.coverImageUrl} alt={project.coverImageAlt ?? ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <CoverArtwork seed={project.slug} label={project.title} />
              )}
            </div>
            <div className="stack" style={{ gap: 8, padding: 22 }}>
              {project.client ? <p className="eyebrow">{project.client}</p> : null}
              <h3>{project.title}</h3>
              <p className="small muted">{project.summary.length > 130 ? `${project.summary.slice(0, 129)}…` : project.summary}</p>
            </div>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

function ProductGrid({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const products = (resolved as ProductCard[]) ?? [];
  if (products.length === 0) return null;

  return (
    <Panel ground={payload.ground}>
      <div className="spread">
        <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
        {payload.ctaLabel && payload.ctaHref ? (
          <Link className="small" style={{ color: 'var(--accent-deep)', fontWeight: 600 }} to={payload.ctaHref}>
            {payload.ctaLabel} →
          </Link>
        ) : null}
      </div>

      <div className="grid" style={{ marginTop: 32, gridTemplateColumns: `repeat(auto-fit, minmax(${payload.columns === 3 ? 250 : 215}px, 1fr))` }}>
        {products.map((product) => (
          <ProductCardView key={product.id} product={product} showAddToCart={payload.showAddToCart !== false} />
        ))}
      </div>
    </Panel>
  );
}

/* ========================================================================== */
/* Simple content sections                                                    */
/* ========================================================================== */

function Stats({ payload }: { payload: P }) {
  const items: { value: string; label: string }[] = payload.items ?? [];
  if (items.length === 0) return null;

  return (
    <section className="section wrap">
      <div className="grid" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))` }}>
        {items.map((item) => (
          <div key={item.label} className="panel" style={{ padding: '34px 28px', textAlign: 'center' }}>
            <p style={{ fontSize: 'var(--t-figure)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--accent-deep)', lineHeight: 1.05 }}>
              {item.value}
            </p>
            <p className="small dim" style={{ marginTop: 8 }}>{item.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const quotes = (resolved as { id: number; quote: string; authorName: string; authorRole: string | null; company: string | null; photoUrl: string | null }[]) ?? [];
  if (quotes.length === 0) return null;

  return (
    <Panel ground={payload.ground}>
      {/* Left, like every other section heading. It was centred, which left it floating
          over a left-aligned grid of cards and out of line with the page's own hero and
          filter row directly above. The logo wall below still centres its heading —
          there the logos are centred too, so the two agree. */}
      <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
      <div className="grid" style={{ marginTop: 34, gridTemplateColumns: `repeat(auto-fit, minmax(280px, 1fr))` }}>
        {quotes.map((quote) => (
          <figure key={quote.id} className="stack" style={{ gap: 16, padding: 28, background: 'var(--panel-2)', borderRadius: 'var(--r-card)' }}>
            <blockquote className="small" style={{ color: 'var(--ink-2)', lineHeight: 1.65 }}>“{quote.quote}”</blockquote>
            <figcaption className="row" style={{ gap: 12, marginTop: 'auto' }}>
              {quote.photoUrl ? (
                <img src={quote.photoUrl} alt="" width={38} height={38} style={{ borderRadius: 999, objectFit: 'cover' }} />
              ) : null}
              <div>
                <p className="small" style={{ fontWeight: 600 }}>{quote.authorName}</p>
                <p className="xs dim">{[quote.authorRole, quote.company].filter(Boolean).join(' · ')}</p>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </Panel>
  );
}

function Team({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const people = (resolved as { id: number; name: string; role: string; bio: string | null; photoUrl: string | null; linkedinUrl: string | null }[]) ?? [];
  if (people.length === 0) return null;

  return (
    <Panel ground={payload.ground}>
      <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
      <div className="grid" style={{ marginTop: 32, gridTemplateColumns: `repeat(auto-fit, minmax(230px, 1fr))` }}>
        {people.map((person) => (
          <div key={person.id} className="card" style={{ background: 'var(--panel-2)' }}>
            <div style={{ aspectRatio: '1 / 1', background: 'var(--wash)' }}>
              {person.photoUrl ? (
                <img src={person.photoUrl} alt={person.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <PersonArtwork name={person.name} />
              )}
            </div>
            <div className="stack" style={{ gap: 6, padding: 20 }}>
              <p style={{ fontWeight: 600 }}>{person.name}</p>
              <p className="xs" style={{ color: 'var(--accent-deep)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{person.role}</p>
              {payload.showBio !== false && person.bio ? <p className="small muted" style={{ marginTop: 4 }}>{person.bio}</p> : null}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Milestones({ payload, resolved }: { payload: P; resolved?: unknown }) {
  const items = (resolved as { id: number; year: number; title: string; description: string | null }[]) ?? [];
  if (items.length === 0) return null;

  return (
    <Panel ground={payload.ground}>
      <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
      <ol className="stack" style={{ marginTop: 32, gap: 0 }}>
        {items.map((item, index) => (
          <li key={item.id} style={{ display: 'grid', gridTemplateColumns: '80px 22px 1fr', gap: 16, alignItems: 'start' }}>
            <span className="mono" style={{ fontSize: 15, color: 'var(--accent-deep)', fontWeight: 600, paddingTop: 14 }}>{item.year}</span>
            {/* The rail: a dot per entry and a line between, so the sequence reads as
                a timeline rather than as a list that happens to have years. */}
            <span style={{ display: 'grid', justifyItems: 'center', height: '100%' }}>
              <span style={{ width: 9, height: 9, borderRadius: 999, background: 'var(--accent)', marginTop: 18 }} />
              {index < items.length - 1 ? <span style={{ width: 1, flex: 1, background: 'var(--hairline)', minHeight: 30 }} /> : null}
            </span>
            <div style={{ padding: '12px 0 26px' }}>
              <p style={{ fontWeight: 600 }}>{item.title}</p>
              {item.description ? <p className="small muted" style={{ marginTop: 5 }}>{item.description}</p> : null}
            </div>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function ImageText({ payload }: { payload: P }) {
  const left = payload.imageSide !== 'right';
  return (
    <Panel ground={payload.ground}>
      <div className={`grid media-split${left ? '' : ' media-right'}`}>
        {payload.media?.url ? (
          <div className="media" style={{ borderRadius: 'var(--r-card)', overflow: 'hidden', aspectRatio: '4 / 3' }}>
            <img src={payload.media.url} alt={payload.media.alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ) : null}
        <div className="stack" style={{ gap: 16 }}>
          <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
          {payload.bullets?.length ? (
            <ul className="stack" style={{ gap: 9 }}>
              {payload.bullets.map((bullet: string) => (
                <li key={bullet} className="row" style={{ gap: 10, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                  <span aria-hidden="true" style={{ color: 'var(--accent-deep)', fontWeight: 700 }}>✓</span>
                  <span className="small muted">{bullet}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <CtaLink cta={payload.cta} />
        </div>
      </div>
    </Panel>
  );
}

function RichText({ payload }: { payload: P }) {
  return (
    <Panel ground={payload.ground}>
      <div style={{ maxWidth: payload.width === 'wide' ? '100%' : 'var(--max-w-text)' }}>
        <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
        <div style={{ marginTop: payload.heading ? 24 : 0 }}>
          <Markdown>{payload.markdown}</Markdown>
        </div>
      </div>
    </Panel>
  );
}

function Faq({ payload }: { payload: P }) {
  const items: { question: string; answer: string }[] = payload.items ?? [];
  return (
    <Panel ground={payload.ground}>
      <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} />
      <div className="stack" style={{ marginTop: 28, gap: 8, maxWidth: 'var(--max-w-text)' }}>
        {items.map((item, index) => (
          // <details> rather than React state: it is keyboard-accessible, findable by
          // the browser's in-page search even when closed, and needs no JavaScript.
          <details key={index} style={{ background: 'var(--panel-2)', borderRadius: 'var(--r-card)', padding: '16px 22px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, listStyle: 'none' }}>{item.question}</summary>
            <p className="small muted" style={{ marginTop: 10 }}>{item.answer}</p>
          </details>
        ))}
      </div>
    </Panel>
  );
}

function CtaSplit({ payload }: { payload: P }) {
  const panels: P[] = payload.panels ?? [];
  return (
    <section className="section wrap">
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {panels.map((panel, index) => {
          const dark = panel.tone === 'dark';
          const accent = panel.tone === 'accent';
          return (
            <div
              key={index}
              className={`panel ${dark ? 'on-dark' : ''}`}
              style={{
                background: dark ? 'var(--ground-dark)' : accent ? 'var(--accent)' : 'var(--panel)',
                color: accent ? 'var(--accent-ink)' : undefined,
                // Two panels across, so the tight step of the scale (tokens.css).
                padding: 'var(--pad-panel-tight)',
                display: 'grid',
                gap: 18,
                alignContent: 'space-between',
              }}
            >
              <div className="stack" style={{ gap: 12 }}>
                <h2 style={{ fontSize: 'var(--t-h3)' }}>{panel.heading}</h2>
                {panel.body ? <p className="small" style={{ color: accent ? 'var(--accent-ink)' : 'var(--ink-2)', opacity: accent ? 0.85 : 1 }}>{panel.body}</p> : null}
              </div>
              {/* Same rule as the CTA banner below: on an accent panel the button has
                  to carry its own contrast, or it reads as a faint outline. */}
              <div><CtaLink cta={panel.cta} className={accent ? 'btn--on-accent' : undefined} /></div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CtaBanner({ payload }: { payload: P }) {
  const accent = payload.tone === 'accent';
  const dark = payload.tone === 'dark';
  return (
    <section className="section wrap">
      <div
        className={`panel ${dark ? 'on-dark' : ''}`}
        style={{
          background: accent ? 'var(--accent)' : dark ? 'var(--ground-dark)' : 'var(--panel)',
          color: accent ? 'var(--accent-ink)' : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 26,
          flexWrap: 'wrap',
        }}
      >
        <div className="stack" style={{ gap: 8 }}>
          {payload.eyebrow ? <p className="eyebrow" style={{ color: accent ? 'var(--accent-ink)' : undefined, opacity: accent ? 0.75 : 1 }}>{payload.eyebrow}</p> : null}
          <h2 style={{ fontSize: 'var(--t-h3)' }}>{payload.heading}</h2>
          {payload.body ? <p className="small" style={{ color: accent ? 'var(--accent-ink)' : 'var(--ink-2)', opacity: accent ? 0.85 : 1, maxWidth: '52ch' }}>{payload.body}</p> : null}
        </div>
        <CtaLink cta={payload.cta} className={accent ? 'btn--on-accent' : undefined} />
      </div>
    </section>
  );
}

function LogoWall({ payload }: { payload: P }) {
  const logos: { name: string; imageUrl: string | null; href: string | null }[] = payload.logos ?? [];
  return (
    <Panel ground={payload.ground}>
      <Heading eyebrow={payload.eyebrow} heading={payload.heading} body={payload.body} align="center" />
      <div className="row" style={{ marginTop: 30, gap: 34, justifyContent: 'center' }}>
        {logos.map((logo) => {
          const content = logo.imageUrl ? (
            <img
              src={logo.imageUrl}
              alt={logo.name}
              style={{ height: 34, objectFit: 'contain', filter: payload.grayscale !== false ? 'grayscale(1)' : undefined, opacity: payload.grayscale !== false ? 0.65 : 1 }}
            />
          ) : (
            <span className="small" style={{ fontWeight: 600, color: 'var(--ink-2)' }}>{logo.name}</span>
          );
          return logo.href ? (
            <a key={logo.name} href={logo.href} target="_blank" rel="noreferrer noopener">{content}</a>
          ) : (
            <span key={logo.name}>{content}</span>
          );
        })}
      </div>
    </Panel>
  );
}

/* ========================================================================== */
/* Registry                                                                   */
/* ========================================================================== */

type SectionComponent = (props: { payload: P; resolved?: unknown }) => ReactNode;

/**
 * Every `SectionType` maps to exactly one component.
 *
 * Typed as a full `Record<SectionType, …>`, so adding a member to `SECTION_TYPES`
 * without adding a component here is a TypeScript error at this line — rather than a
 * section that saves cleanly in the editor and renders as nothing on the live site.
 */
const REGISTRY: Record<SectionType, SectionComponent> = {
  hero: Hero,
  marquee: Marquee,
  trust_bar: TrustBar,
  capabilities: Capabilities,
  configurator: Configurator,
  practice: Practice,
  deployment_record: DeploymentRecord,
  contact_split: ContactSplit,
  services_grid: ServicesGrid,
  featured_project: FeaturedProject,
  project_grid: ProjectGrid,
  product_grid: ProductGrid,
  stats: Stats,
  testimonials: Testimonials,
  team: Team,
  milestones: Milestones,
  image_text: ImageText,
  rich_text: RichText,
  faq: Faq,
  cta_split: CtaSplit,
  cta_banner: CtaBanner,
  contact_form: ContactFormSection,
  newsletter: NewsletterSection,
  logo_wall: LogoWall,
};

/**
 * Render one stored section.
 *
 * An unknown type renders NOTHING rather than throwing. The database can outlive this
 * bundle — a section saved by a newer deploy and then rolled back would otherwise take
 * the whole page down with it, and a missing band is a far better failure than a white
 * screen.
 */
export function RenderSection({ section }: { section: SectionData }) {
  const Component = REGISTRY[section.type as SectionType];
  if (!Component) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`No component registered for section type "${section.type}"`);
    }
    return null;
  }
  return <Component payload={section.payload as P} resolved={section.resolved} />;
}

export function RenderSections({ sections }: { sections: SectionData[] }) {
  return (
    <>
      {sections.filter((s) => s.isEnabled).map((section) => (
        <RenderSection key={section.id} section={section} />
      ))}
    </>
  );
}

export { Money, StockPill };
